import { useCallback, useState } from "react";

import { getFormatKey, ImageSize, resolveImageUrl, resolveVideoUrl } from "@/libs/assetPreloading/preloadConfig";
import { usePreloadStore } from "@/libs/assetPreloading/usePreloadStore";
import { StrapiAsset } from "@/types/api";

type Props = {
    image: StrapiAsset;
    /** Determines which Strapi format to render — must match the category used at preload time */
    size: ImageSize;
    alt?: string;
    className?: string;
    loading?: "lazy" | "eager";
    priority?: boolean;
    objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
    onLoad?: () => void;
    onError?: () => void;
    useImageAspectRatio?: boolean;
    videoRef?: React.RefObject<HTMLVideoElement | null>;
};

function isVideo(media: StrapiAsset): boolean {
    if ("mime" in media && typeof media.mime === "string") {
        return media.mime.startsWith("video/");
    }
    if ("url" in media && typeof media.url === "string") {
        return /\.(mp4|webm|ogg)(\?|$)/i.test(media.url);
    }
    return false;
}

/**
 * Returns the width and height for the selected Strapi format.
 * Falls back to original media dimensions if the format doesn't exist
 * (e.g. SVGs or images smaller than the format breakpoint).
 */
function getFormatDimensions(media: StrapiAsset, size: ImageSize) {
    const format = media.formats?.[getFormatKey(size)];
    return {
        width: format?.width ?? media.width,
        height: format?.height ?? media.height,
    };
}

/**
 * StrapiMedia Component
 *
 * Renders a responsive image or a muted autoplay video.
 *
 * Uses deterministic format selection via the size prop instead of srcset/sizes.
 * This guarantees the rendered URL matches the preloaded URL, preventing
 * redundant downloads. See preloadConfig.ts for the rationale.
 *
 * The img width/height attributes are set to the selected format's dimensions,
 * not the original media dimensions. This ensures the browser allocates GPU
 * textures at the actual resource size, preventing excessive memory usage
 * on mobile devices (especially iOS Safari).
 */
export function StrapiMedia({
    image: media,
    size,
    alt,
    className,
    loading,
    priority = false,
    objectFit = "cover",
    onLoad,
    onError,
    useImageAspectRatio = true,
    videoRef,
}: Props) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const getVideoSrc = usePreloadStore((s) => s.getVideoSrc);

    const handleLoad = useCallback(() => {
        setIsLoaded(true);
        onLoad?.();
    }, [onLoad]);

    const handleError = useCallback(() => {
        setHasError(true);
        onError?.();
    }, [onError]);

    const altText = alt ?? media.alternativeText ?? "";

    if (isVideo(media)) {
        const src = resolveVideoUrl(media);
        const cachedSrc = getVideoSrc(src);

        const aspectRatio =
            useImageAspectRatio && media.width && media.height ? `${media.width} / ${media.height}` : undefined;

        return (
            <video
                ref={videoRef}
                crossOrigin="anonymous"
                src={cachedSrc}
                autoPlay
                muted
                loop
                playsInline
                width={media.width ?? undefined}
                height={media.height ?? undefined}
                onLoadedData={handleLoad}
                onError={handleError}
                className={className}
                style={{ objectFit, aspectRatio }}
                data-loaded={isLoaded}
                data-error={hasError}
            >
                <track kind="descriptions" label={altText} />
            </video>
        );
    }

    const { width, height } = getFormatDimensions(media, size);
    const src = resolveImageUrl(media, size);
    const loadingAttr = priority ? undefined : (loading ?? "lazy");
    const fetchPriority = priority ? "high" : undefined;

    const aspectRatio = useImageAspectRatio && width && height ? `${width} / ${height}` : undefined;

    return (
        <img
            src={src}
            alt={altText}
            width={width!}
            height={height!}
            loading={loadingAttr}
            fetchPriority={fetchPriority}
            decoding={priority ? "sync" : "async"}
            onLoad={handleLoad}
            onError={handleError}
            className={className}
            style={{ objectFit, aspectRatio }}
            data-loaded={isLoaded}
            data-error={hasError}
        />
    );
}
